import { HttpResponse } from "@angular/common/http";
import { Component, Inject } from "@angular/core";
import { AbstractControl, FormControl, FormGroup } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { Sort } from "@angular/material/sort";
import { extractFilenameFromExportHeaders } from "@app/_helpers/extract-filename-from-content-disposition";
import { createRxValue, sanitizeFilename } from "@app/_helpers/utils";
import { TranslateService } from "@ngx-translate/core";
import { ApplicationSettingsQuery, MyTimesService, UserSettingsQuery } from "@timeghost/web-api";
import { endOfDay, format, isSameDay, startOfDay } from "date-fns/esm";
import { saveAs } from "file-saver";

type ContingentExportConfig = {
	type: "csv" | "excel";
	users: string[];
	userWithOutStart: boolean;
	sortBy: Sort;
	cutOffDate: Date;
	maxCutOffDate?: Date;
};
type ContingentExportsSettings = {
	type?: ContingentExportConfig["type"];
};
type ContingentGroup = { [K in keyof ContingentExportConfig]: AbstractControl<ContingentExportConfig[K]> };
export const resolveFileExtFromType = (type: "csv" | "excel") => {
	if (type === "csv") return "csv";
	if (type === "excel") return "xlsx";
	return null;
};
@Component({
	selector: "tg-contingent-export-dialog",
	templateUrl: "./contingent-export-dialog.component.html",
	styleUrl: "./contingent-export-dialog.component.scss",
})
export class ContingentExportDialogComponent {
	constructor(
		private myTimesService: MyTimesService,
		private ref: MatDialogRef<ContingentExportDialogComponent>,
		@Inject(MAT_DIALOG_DATA)
		private data: ContingentExportConfig,
		private translate: TranslateService,
		private userSettingsQuery: UserSettingsQuery,
		private appSettingsQuery: ApplicationSettingsQuery
	) {}
	readonly group = new FormGroup<ContingentGroup>({
		type: new FormControl<ContingentExportConfig["type"]>("excel"),
		users: new FormControl([]),
		userWithOutStart: new FormControl<boolean>(false),
		sortBy: new FormControl<Sort>({ active: "contingent", direction: "asc" }),
		cutOffDate: new FormControl<Date>(new Date()),
	});
	readonly fileTypeOptions: ReadonlyArray<{
		value: ContingentExportConfig["type"];
		title: string;
		description: string;
		icon: string;
	}> = [
		{ value: "excel", title: "Excel", description: ".xlsx", icon: "table_view" },
		{ value: "csv", title: "CSV", description: ".csv", icon: "description" },
	];
	readonly loading$ = createRxValue(false);
	maxCutOffDate: Date | null = null;
	get config(): ContingentExportsSettings {
		return ((this.appSettingsQuery.getValue() as any)?.contingent?.export ?? {}) as ContingentExportsSettings;
	}
	set config(value: ContingentExportsSettings) {
		this.appSettingsQuery.updateByKey("contingent.export", value);
	}
	get closeDisabled() {
		return this.ref.disableClose;
	}
	ngOnInit() {
		this.maxCutOffDate = this.data.maxCutOffDate ? startOfDay(new Date(this.data.maxCutOffDate)) : null;
		const savedConfig = this.config;
		const selectedCutOffDate = this.data.cutOffDate
			? startOfDay(new Date(this.data.cutOffDate))
			: startOfDay(new Date());
		const initialCutOffDate =
			this.maxCutOffDate && selectedCutOffDate > this.maxCutOffDate ? this.maxCutOffDate : selectedCutOffDate;
		this.ref.updateSize("680px");
		this.group.patchValue({
			type: savedConfig.type ?? this.data.type,
			users: this.data.users?.length ? this.data.users : [],
			userWithOutStart: this.data.userWithOutStart,
			sortBy: this.data.sortBy,
			cutOffDate: initialCutOffDate,
		});
	}
	private persistConfig(type: ContingentExportConfig["type"]) {
		this.config = {
			type,
		};
	}
	async submit() {
		this.loading$.next(true);
		this.ref.disableClose = true;
		this.group.disable();
		const config = this.group.getRawValue();
		const cutOffDate = config.cutOffDate ? startOfDay(new Date(config.cutOffDate)) : null;
		this.persistConfig(config.type);
		const user = this.userSettingsQuery.getValue();
		const userDateFormat = user.settings.dateFormat;
		await this.myTimesService.adapter
			.post(
				"/export_workinghours_contingent",
				{
					type: config.type,
					users: config.users.length ? config.users : "all",
					includeToday: false,
					includeUsersWithoutStart: true,
					cutOffDate:
						cutOffDate && !isSameDay(cutOffDate, new Date()) ? endOfDay(cutOffDate).toISOString() : null,
					sort: "alphabet",
					sortDirection: config.sortBy.direction == "asc" ? "asc" : "desc",
					sortContext: config.sortBy?.active ?? "contingent",
					pinCurrentUser: false,
				},
				{ responseType: "blob" as "json", observe: "response" }
			)
			.toPromise()
			.then((x: HttpResponse<Blob>) => {
				const fileExt = resolveFileExtFromType(config.type);
				if (!fileExt) throw new Error("Invalid file extension");
				if (!x.body) throw new Error("Export response body is empty");
				const headerFileName = extractFilenameFromExportHeaders(x.headers);
				let filename = "";
				if (fileExt == "csv") {
					filename = `${sanitizeFilename(user.workspace.name)}-${sanitizeFilename(format(new Date(), userDateFormat))}-${sanitizeFilename(format(new Date(), "HH:mm"))}`;
				} else
					filename = `${sanitizeFilename(user.workspace.name)}-${sanitizeFilename(format(new Date(), userDateFormat))}`;
				saveAs(x.body, headerFileName ?? `${filename}.${fileExt}`);
				this.ref.close();
			})
			.finally(() => {
				this.loading$.next(false);
				this.ref.disableClose = false;
				this.group.enable();
			});
	}
}
